import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Loader, Btn, Field, TextInput, SelectInput, EmptyState } from "../components/Primitives";
import { StatusBadge } from "../components/Badges";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Sparkles, FileEdit, Download } from "lucide-react";
import { toast } from "sonner";

const DOC_GROUPS = {
  "Core policies": ["Privacy Notice", "Consent Form", "Data Retention Policy", "Breach Response Policy"],
  "Rights & grievance": ["Data Principal Rights SOP", "Grievance Redressal Policy", "Data Deletion SOP"],
  "Vendors & contracts": ["Vendor DPDP Clause", "Processor Agreement Checklist"],
  "Cross-border transfers": [
    "Cross-Border Transfer Policy",
    "Standard Contractual Clauses (SCC)",
    "Cross-Border Transfer Assessment",
    "Data Transfer Impact Assessment (TIA)",
  ],
  "Special categories": ["Employee Privacy Policy", "Customer Privacy Policy", "Children's Data Policy"],
};

export default function PolicyGenerator() {
  const [docs, setDocs] = useState(null);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null);
  const [form, setForm] = useState({ doc_type: "Privacy Notice", title: "" });
  const [genLoading, setGenLoading] = useState(false);

  const load = async () => { const { data } = await api.get("/documents", { params: { limit: 200 } }); setDocs(Array.isArray(data) ? data : (data.items || [])); };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenLoading(true);
    try {
      const payload = { doc_type: form.doc_type, title: form.title || form.doc_type };
      await api.post("/documents/generate", payload);
      toast.success("Document generated"); setOpen(false); load();
    } catch { toast.error("Failed"); }
    setGenLoading(false);
  };

  const download = async (d, format = "pdf") => {
    const res = await api.get(`/documents/${d.id}/export?format=${format}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `${d.title.slice(0, 40)}.${format}`; a.click();
  };

  if (!docs) return <Loader />;

  return (
    <div>
      <PageHeader
        title="Policy & Document Generator"
        description="AI-drafted DPDP policies, SOPs, notices, and contract clauses ready for legal review."
        testId="policy-page-header"
        actions={<Btn data-testid="policy-generate-btn" onClick={() => setOpen(true)}><Sparkles className="w-4 h-4" /> Generate with AI</Btn>}
      />

      {docs.length === 0 ? <EmptyState title="No documents yet" hint="Generate your first DPDP policy or notice using the AI drafting engine." /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {docs.map((d) => (
            <div key={d.id} className="bg-white border border-slate-200 rounded-md p-4 hover:border-slate-300">
              <div className="flex items-start justify-between mb-2">
                <FileEdit className="w-4 h-4 text-blue-600" />
                <StatusBadge status={d.status} />
              </div>
              <div className="text-sm font-semibold text-slate-900">{d.title}</div>
              <div className="text-[11px] text-slate-500 uppercase tracking-wider mt-1">{d.doc_type} · v{d.version}</div>
              <div className="text-xs text-slate-600 mt-2 line-clamp-3">{d.content?.slice(0, 200)}</div>
              <div className="mt-3 flex gap-2">
                <Btn size="sm" variant="secondary" data-testid={`policy-view-${d.id}`} onClick={() => setView(d)}>View</Btn>
                <Btn size="sm" variant="secondary" data-testid={`policy-download-${d.id}`} onClick={() => download(d, "pdf")}><Download className="w-3.5 h-3.5" /> PDF</Btn>
                <Btn size="sm" variant="secondary" data-testid={`policy-download-docx-${d.id}`} onClick={() => download(d, "docx")}>Word</Btn>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>AI Document Generator</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Document type" required>
              <SelectInput data-testid="policy-doctype-select" value={form.doc_type} onChange={(e) => setForm({...form, doc_type: e.target.value})}>
                {Object.entries(DOC_GROUPS).map(([grp, types]) => (
                  <optgroup key={grp} label={grp}>
                    {types.map(o => <option key={o} value={o}>{o}</option>)}
                  </optgroup>
                ))}
              </SelectInput>
            </Field>
            <Field label="Title (optional)"><TextInput value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} /></Field>
            <div className="text-xs text-slate-500">The AI will draft a DPDP-aligned document. All output includes a legal disclaimer.</div>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn data-testid="policy-do-generate-btn" onClick={generate} disabled={genLoading}>{genLoading ? "Generating..." : "Generate"}</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!view} onOpenChange={(v) => !v && setView(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{view?.title}</DialogTitle></DialogHeader>
          <div className="max-h-[65vh] overflow-y-auto text-sm whitespace-pre-wrap pr-1">{view?.content}</div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
